import React, { useRef, useState } from 'react';
import { useParams } from 'react-router';

// Types
import {
  ReviewFormTemplateType,
  FormTypeType,
  QuestionType,
  QuestionTypeType,
} from 'Shared/types/ReviewFormTemplate.type';

// Utils
import { generateQuestionKey } from './generateQuestionKey.utils';

// Hooks
import { useReviewTemplate } from 'Shared/utils/hooks/use-review-template';
import { useReviewFormTemplate } from 'Shared/utils/hooks/use-review-form-template';

// Components
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import Question from './Question';
import EmptyStateContainer from 'Shared/components/EmptyStateContainer/EmptyStateContainer';

// Styles
import './TemplateQuestions.scss';

type Props = {
  reviewFormType: FormTypeType;
};

const TemplateQuestions = ({ reviewFormType }: Props) => {
  const { slug } = useParams();
  const [activeQuestionKey, setActiveQuestionKey] = useState<string>(null);

  const { reviewFormTemplates, handleUpdateReviewFormTemplate } = useReviewTemplate({
    slug,
    withFetch: false,
  });
  const addBtnRef = useRef(null);

  const reviewFormTemplate: ReviewFormTemplateType = reviewFormTemplates.find(
    template => template.formType === reviewFormType
  );

  const { questionCount } = useReviewFormTemplate({ reviewFormTemplate });

  const handleAddQuestionClick = () => {
    const newQuestionKey = generateQuestionKey({ prefix: 'question_', removeDashes: true, lettersOnly: true });
    const questions = [
      ...reviewFormTemplate.questions,
      {
        key: newQuestionKey,
        type: QuestionTypeType.TEXTAREA,
        header: '',
        description: '',
        placeholder: '',
      } as QuestionType,
    ];
    const newReviewFormTemplate = { ...reviewFormTemplate, questions };
    handleUpdateReviewFormTemplate({ reviewFormTemplate: newReviewFormTemplate }).then(() => {
      setActiveQuestionKey(newQuestionKey);
      setTimeout(() => addBtnRef.current.scrollIntoView(false), 100);
    });
  };

  const handleUpdateQuestion = ({ question }: QuestionType) => {
    const questionIndex = reviewFormTemplate.questions.findIndex(q => q.key === question.key);
    const questions = reviewFormTemplate.questions;
    questions[questionIndex] = question;
    const newReviewFormTemplate = { ...reviewFormTemplate, questions };
    handleUpdateReviewFormTemplate({ reviewFormTemplate: newReviewFormTemplate, withDebounce: true });
  };

  const handleDeleteQuestion = ({ question }: QuestionType) => {
    const questions = [...reviewFormTemplate.questions.filter(q => q.key !== question.key)];
    const newReviewFormTemplate = { ...reviewFormTemplate, questions };
    handleUpdateReviewFormTemplate({ reviewFormTemplate: newReviewFormTemplate, withDebounce: true });
  };

  const handleDuplicateQuestion = ({ question }: QuestionType) => {
    const newQuestionKey = generateQuestionKey({ prefix: 'question_', removeDashes: true, lettersOnly: true });

    const questionIndex = reviewFormTemplate.questions.findIndex(q => q.key === question.key);
    const questions = reviewFormTemplate.questions;

    const newQuestions = [
      ...questions.slice(0, questionIndex + 1),
      { ...question, key: newQuestionKey },
      ...questions.slice(questionIndex + 1),
    ];
    const newReviewFormTemplate = { ...reviewFormTemplate, questions: newQuestions };
    handleUpdateReviewFormTemplate({ reviewFormTemplate: newReviewFormTemplate, withDebounce: true }).then(() => {
      setActiveQuestionKey(newQuestionKey);
    });
  };

  const handleMoveQuestion = ({ direction, question }: { direction: 'up' | 'down'; question: QuestionType }) => {
    const questionIndex = reviewFormTemplate.questions.findIndex(q => q.key === question.key);
    const questions = reviewFormTemplate.questions;

    const swapQuestions = toSwapIndex => {
      questions[questionIndex] = questions.splice(toSwapIndex, 1, questions[questionIndex])[0];
    };

    if (direction === 'down' && questionIndex !== reviewFormTemplate.questions.length - 1) {
      swapQuestions(questionIndex + 1);
      const newReviewFormTemplate = { ...reviewFormTemplate, questions };
      handleUpdateReviewFormTemplate({ reviewFormTemplate: newReviewFormTemplate, withDebounce: false });
    } else if (direction === 'up' && questionIndex !== 0) {
      swapQuestions(questionIndex - 1);
      const newReviewFormTemplate = { ...reviewFormTemplate, questions };
      handleUpdateReviewFormTemplate({ reviewFormTemplate: newReviewFormTemplate, withDebounce: false });
    }
  };

  const baseClassName = 'TemplateQuestions';

  if (questionCount === 0) {
    return (
      <div className={baseClassName}>
        <div className={`${baseClassName}__empty`}>
          <EmptyStateContainer
            title="Add content to get started"
            titleTag="h5"
            button={
              <WpButton onClick={handleAddQuestionClick}>
                <WpIcon name="plus" />
                Add content
              </WpButton>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__list`}>
        {reviewFormTemplate.questions.map(question => (
          <div className={`${baseClassName}__list__question`} key={question.key}>
            <Question
              question={question}
              onChange={handleUpdateQuestion}
              handleDeleteQuestion={handleDeleteQuestion}
              handleDuplicateQuestion={handleDuplicateQuestion}
              activeQuestionKey={activeQuestionKey}
              setActiveQuestionKey={setActiveQuestionKey}
              handleMoveQuestion={handleMoveQuestion}
              reviewFormType={reviewFormType}
              reviewFormTemplates={reviewFormTemplates}
            />
          </div>
        ))}
      </div>
      <WpButton onClick={handleAddQuestionClick} ref={addBtnRef}>
        <WpIcon name="plus" />
        Add Content
      </WpButton>
    </div>
  );
};

export default TemplateQuestions;
